/**
 * Accessibility utilities for ARIA labels and semantic HTML
 */

/**
 * Generate unique IDs for ARIA relationships
 */
let idCounter = 0;
export const generateId = (prefix: string = 'a11y'): string => {
  idCounter += 1;
  return `${prefix}-${idCounter}-${Date.now()}`;
};

/**
 * Get ARIA label for navigation items
 */
export const getNavAriaLabel = (label: string, isActive: boolean, unreadCount?: number): string => {
  let ariaLabel = label;
  if (isActive) {
    ariaLabel += ', current page';
  }
  if (unreadCount && unreadCount > 0) {
    ariaLabel += `, ${unreadCount} unread`;
  }
  return ariaLabel;
};

/**
 * Get ARIA label for buttons with loading state
 */
export const getButtonAriaLabel = (label: string, isLoading: boolean): string => {
  return isLoading ? `${label}, loading` : label;
};

/**
 * Get ARIA label for form fields with validation
 */
export const getFieldAriaLabel = (
  label: string,
  required: boolean,
  error?: string
): { 'aria-label': string; 'aria-required'?: boolean; 'aria-invalid'?: boolean } => {
  let ariaLabel = label;
  if (required) {
    ariaLabel += ', required';
  }
  if (error) {
    ariaLabel += `, error: ${error}`;
  }

  return {
    'aria-label': ariaLabel,
    ...(required && { 'aria-required': true }),
    ...(error && { 'aria-invalid': true }),
  };
};

/**
 * Get ARIA label for list items
 */
export const getListItemAriaLabel = (
  itemName: string,
  index: number,
  total: number,
  additionalInfo?: string
): string => {
  let ariaLabel = `${itemName}, item ${index + 1} of ${total}`;
  if (additionalInfo) {
    ariaLabel += `, ${additionalInfo}`;
  }
  return ariaLabel;
};

/**
 * Get ARIA label for status indicators
 */
export const getStatusAriaLabel = (status: string, context?: string): string => {
  let ariaLabel = `Status: ${status}`;
  if (context) {
    ariaLabel += ` for ${context}`;
  }
  return ariaLabel;
};

/**
 * Get ARIA label for date/time
 */
export const getDateTimeAriaLabel = (date: Date, includeTime: boolean = true): string => {
  const dateStr = date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (!includeTime) {
    return dateStr;
  }

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  return `${dateStr} at ${timeStr}`;
};

/**
 * Get ARIA label for image with fallback
 */
export const getImageAriaLabel = (alt: string, name?: string): string => {
  if (alt) return alt;
  if (name) return `Image of ${name}`;
  return 'Image';
};

/**
 * Get ARIA live region announcement
 */
export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Screen reader only CSS class
 */
export const srOnlyStyles = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
} as const;

/**
 * Get heading level based on page hierarchy
 */
export const getHeadingLevel = (level: 1 | 2 | 3 | 4 | 5 | 6): `h${typeof level}` => {
  return `h${level}`;
};

/**
 * Validate heading hierarchy
 */
export const validateHeadingHierarchy = (): boolean => {
  const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
  let previousLevel = 0;

  for (const heading of headings) {
    const level = parseInt(heading.tagName.substring(1), 10);

    // Check if heading level skips more than one level
    if (level - previousLevel > 1) {
      console.warn(
        `Heading hierarchy violation: ${heading.tagName} follows h${previousLevel}`,
        heading
      );
      return false;
    }

    previousLevel = level;
  }

  return true;
};

/**
 * Get ARIA label for pagination
 */
export const getPaginationAriaLabel = (
  currentPage: number,
  totalPages: number,
  itemsPerPage: number
): string => {
  return `Page ${currentPage} of ${totalPages}, showing ${itemsPerPage} items per page`;
};

/**
 * Get ARIA label for search results
 */
export const getSearchResultsAriaLabel = (count: number, query: string): string => {
  if (count === 0) {
    return `No results found for "${query}"`;
  }
  if (count === 1) {
    return `1 result found for "${query}"`;
  }
  return `${count} results found for "${query}"`;
};

/**
 * Get ARIA label for modal/dialog
 */
export const getDialogAriaLabel = (title: string, description?: string): {
  'aria-labelledby': string;
  'aria-describedby'?: string;
} => {
  const labelId = generateId('dialog-title');
  const descId = description ? generateId('dialog-desc') : undefined;

  return {
    'aria-labelledby': labelId,
    ...(descId && { 'aria-describedby': descId }),
  };
};
